
import { useRef, useState } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { Link } from '@tanstack/react-router'
import { Icon } from '@/components/ui/icon'
import { Button } from '@/components/ui/button'
import { InlineRename } from '@/components/ui/inline-rename'
import { DropdownMenuWrapper as DropdownMenu } from '@/components/ui/dropdown-menu/wrapper'
import { Folder } from './types'

const ItemTypes = {
  FOLDER: 'folder',
}

interface FolderItemProps {
  folder: Folder
  index: number
  isEditing: boolean
  isUpdating?: boolean
  onEdit: () => void
  onCancelEdit: () => void
  onRename: (newName: string) => void
  onDelete: () => void
  onMove: (fromIndex: number, toIndex: number) => void
}

export const FolderItem: React.FC<FolderItemProps> = ({
  folder,
  index,
  isEditing,
  isUpdating = false,
  onEdit,
  onCancelEdit,
  onRename,
  onDelete,
  onMove,
}) => {
  const ref = useRef<HTMLLIElement>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isRenaming, setIsRenaming] = useState(false)

  const canDrag = folder.isDraggable !== false
  const canDelete = folder.canDelete !== false
  const canRename = folder.canRename !== false

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.FOLDER,
    item: { index, id: folder.id },
    canDrag,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  const [, drop] = useDrop({
    accept: ItemTypes.FOLDER,
    drop(item: { index: number; id: string }) {
      // Simple: only API call when drop happens
      if (item.index !== index) {
        onMove(item.index, index)
      }
    },
  })

  // if canDrag is true, drag the row
  if (canDrag) {
    drag(ref)
  }
  drop(ref)

  const handleRename = async (newName: string) => {
    if (isRenaming) return
    setIsRenaming(true)
    await new Promise((resolve) => setTimeout(resolve, 300))
    onRename(newName)
    setIsRenaming(false)
  }

  return (
    <li
      ref={ref}
      className={`transition-all duration-200 ease-in-out ${
        isDragging
          ? 'z-50 scale-[1.02] opacity-70 rotate-[1deg] cursor-grabbing'
          : 'cursor-grab'
      }`}
    >
      <div
        className={`group folder-item flex items-center justify-between ${
          folder.isActive ? 'active' : ''
        } h-8 rounded-md px-2 py-1 transition-all duration-200 hover:bg-accent/50`}
      >
        {isEditing ? (
          <InlineRename
            value={folder.name}
            onSave={handleRename}
            onCancel={onCancelEdit}
            isEditing={true}
            isLoading={isUpdating}
            variant="ghost"
            size="sm"
            className="flex-1 gap-0"
            iconBefore={
              <Icon name="folder" className="text-foreground mr-2 h-4 w-4" />
            }
            textClassName={`text-xs truncate ${
              folder.isActive
                ? 'text-foreground font-medium'
                : 'text-foreground'
            }`}
            inputClassName="font-inherit"
            showSaveButton={true}
          />
        ) : (
          <>
            {/* Folder Content */}
            <Link
              to={folder.href}
              className="flex min-w-0 flex-1 items-center hover:no-underline"
            >
              <Icon
                name="folder"
                className={`text-foreground mr-2 h-4 w-4 ${
                  canDrag ? 'cursor-grab' : ''
                }`}
              />
              <span
                className={`truncate text-xs ${
                  folder.isActive
                    ? 'text-foreground font-medium'
                    : 'text-foreground'
                }`}
                title={folder.name}
              >
                {folder.name}
              </span>
            </Link>

            {/* Actions Menu */}
            {(canRename || canDelete) && (
              <DropdownMenu
                items={[
                  {
                    type: 'item',
                    label: 'Rename folder',
                    onClick: onEdit,
                    disabled: !canRename,
                  },
                  {
                    type: 'item',
                    label: 'Delete folder',
                    variant: 'danger',
                    onClick: onDelete,
                    disabled: !canDelete,
                  },
                ]}
                align="start"
                open={isMenuOpen}
                onOpenChange={setIsMenuOpen}
                modal={false}
              >
                <Button
                  variant="ghost"
                  size="sm"
                  className={`h-6 w-6 p-0 ${
                    isMenuOpen
                      ? 'opacity-100'
                      : 'opacity-0 group-hover:opacity-100'
                  } text-muted-foreground hover:text-foreground hover:bg-muted`}
                >
                  <Icon name="more-horizontal" className="h-3 w-3" />
                </Button>
              </DropdownMenu>
            )}
          </>
        )}
      </div>
    </li>
  )
}
